<template>
    <div>
        <div v-if="loadingMethods" class="text-center py-3">
            <i class="fas fa-spinner fa-spin"></i> Loading metode...
        </div>
        <div v-else-if="!methods.manual_transfer_enabled" class="alert alert-warning">
            Transfer manual sedang nonaktif. Silakan gunakan Tripay atau hubungi admin.
        </div>
        <div v-else>
            <div class="alert alert-info" v-if="methods.instruction_text">
                <strong><i class="fas fa-info-circle"></i> Instruksi:</strong>
                <div style="white-space: pre-wrap;">{{ methods.instruction_text }}</div>
                <div v-if="methods.contact_admin_for_help" class="mt-2">
                    <strong>{{ methods.contact_admin_for_help }}</strong>
                </div>
            </div>

            <!-- Summary -->
            <div class="card p-3 mb-3 bg-light">
                <div class="d-flex justify-content-between">
                    <span>Total yang harus ditransfer:</span>
                    <strong class="text-primary">{{ formatRupiah(amount) }}</strong>
                </div>
                <div v-if="targetLabel" class="text-muted small mt-1">
                    Untuk: <strong>{{ targetLabel }}</strong>
                </div>
            </div>

            <!-- Bank list -->
            <h6 class="mb-2"><i class="fas fa-university"></i> Pilih Bank Tujuan</h6>
            <div class="row mb-3">
                <div v-for="b in methods.banks" :key="b.id" class="col-md-6 mb-2">
                    <label class="card p-3 d-block" style="cursor:pointer;"
                        :style="form.bank_account_id === b.id ? 'border:2px solid #28a745;' : ''">
                        <div class="d-flex align-items-start">
                            <input type="radio" :value="b.id" v-model="form.bank_account_id" class="mr-2 mt-1">
                            <div class="flex-grow-1">
                                <div><strong>{{ b.bank_name }}</strong></div>
                                <div><code>{{ b.account_number }}</code></div>
                                <div class="text-muted small">a.n. {{ b.account_holder }}</div>
                                <div v-if="b.note" class="text-info small mt-1"><em>{{ b.note }}</em></div>
                            </div>
                        </div>
                    </label>
                </div>
                <div v-if="!methods.banks || methods.banks.length === 0" class="col-12">
                    <div class="alert alert-warning">Belum ada rekening tersedia. Hubungi admin.</div>
                </div>
            </div>

            <!-- Sender info -->
            <h6 class="mb-2"><i class="fas fa-user"></i> Data Pengirim</h6>
            <div class="row mb-3">
                <div class="col-md-6 mb-2">
                    <label>Nama Pengirim *</label>
                    <input v-model="form.sender_name" class="form-control" placeholder="Nama lengkap di rekening">
                </div>
                <div class="col-md-3 mb-2">
                    <label>Bank Pengirim</label>
                    <input v-model="form.sender_bank" class="form-control" placeholder="BCA / Mandiri / OVO">
                </div>
                <div class="col-md-3 mb-2">
                    <label>No. Rek Pengirim</label>
                    <input v-model="form.sender_account" class="form-control" placeholder="opsional">
                </div>
                <div class="col-md-6 mb-2">
                    <label>Tanggal Transfer *</label>
                    <input v-model="form.transfer_date_str" type="datetime-local" class="form-control">
                </div>
                <div class="col-md-6 mb-2">
                    <label>Bukti Transfer * <small class="text-muted">(image, max 5MB)</small></label>
                    <input type="file" ref="fileInput" accept="image/*" @change="onFileChange" class="form-control">
                </div>
                <div class="col-md-12 mb-2">
                    <label>Catatan (opsional)</label>
                    <textarea v-model="form.user_note" class="form-control" rows="2"
                        placeholder="Catatan tambahan..."></textarea>
                </div>
            </div>

            <div class="d-flex gap-2">
                <button @click="submit" :disabled="submitting" class="btn btn-success mr-2">
                    <i class="fas fa-paper-plane"></i>
                    {{ submitting ? 'Mengirim...' : 'Kirim Konfirmasi Transfer' }}
                </button>
                <button @click="$emit('cancel')" class="btn btn-secondary">Batal</button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ManualTransferForm',
    props: {
        paymentMode: { type: String, required: true },  // 'topup' | 'buy_package' | 'topup_chatgpt' | 'addon' | 'shop_order'
        amount: { type: Number, required: true },
        targetId: { type: Number, default: 0 },
        targetLabel: { type: String, default: '' },
    },
    data() {
        return {
            loadingMethods: true,
            submitting: false,
            methods: { manual_transfer_enabled: false, banks: [], instruction_text: '' },
            form: {
                bank_account_id: null,
                sender_name: '',
                sender_account: '',
                sender_bank: '',
                transfer_date_str: '',
                user_note: '',
            },
            proofFile: null,
        };
    },
    mounted() {
        this.loadMethods();
        // Default transfer date to "now"
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        this.form.transfer_date_str =
            `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    },
    methods: {
        formatRupiah(n) {
            return 'Rp ' + Number(n || 0).toLocaleString('id-ID');
        },
        notify(type, msg) {
            if (this.$toast) {
                this.$toast.open({ message: msg, type, duration: type === 'error' ? 6000 : 3000, position: 'top-right' });
            } else {
                window.alert(msg);
            }
        },
        async loadMethods() {
            this.loadingMethods = true;
            try {
                const r = await axios.get('payment/methods');
                if (r.data && r.data.success) {
                    this.methods = r.data.data || {};
                }
            } catch (e) {
                this.notify('error', 'Gagal load metode: ' + ((e.response && e.response.data && e.response.data.message) || e.message));
            } finally {
                this.loadingMethods = false;
            }
        },
        onFileChange(ev) {
            const f = ev.target.files && ev.target.files[0];
            if (!f) return;
            if (f.size > 5 * 1024 * 1024) {
                this.notify('warning', 'Ukuran file maksimal 5MB');
                this.$refs.fileInput.value = '';
                return;
            }
            this.proofFile = f;
        },
        async submit() {
            if (!this.form.bank_account_id) {
                this.notify('warning', 'Pilih bank tujuan transfer');
                return;
            }
            if (!this.form.sender_name || !this.form.sender_name.trim()) {
                this.notify('warning', 'Isi nama pengirim');
                return;
            }
            if (!this.proofFile) {
                this.notify('warning', 'Upload bukti transfer');
                return;
            }

            this.submitting = true;
            try {
                const fd = new FormData();
                fd.append('payment_mode', this.paymentMode);
                fd.append('amount', String(this.amount));
                if (this.targetId) fd.append('target_id', String(this.targetId));
                if (this.targetLabel) fd.append('target_label', this.targetLabel);
                fd.append('bank_account_id', String(this.form.bank_account_id));
                fd.append('sender_name', this.form.sender_name);
                if (this.form.sender_account) fd.append('sender_account', this.form.sender_account);
                if (this.form.sender_bank) fd.append('sender_bank', this.form.sender_bank);
                if (this.form.user_note) fd.append('user_note', this.form.user_note);
                if (this.form.transfer_date_str) {
                    const td = new Date(this.form.transfer_date_str).getTime();
                    if (td > 0) fd.append('transfer_date', String(td));
                }
                fd.append('proof_image', this.proofFile);

                const r = await axios.post('payment/manual', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                if (r.data && r.data.success) {
                    this.notify('success', 'Konfirmasi berhasil dikirim. Admin akan verify dalam max 1x24 jam.');
                    this.$emit('success', r.data.data && r.data.data.payment);
                } else {
                    this.notify('error', 'Gagal: ' + (r.data && r.data.message));
                }
            } catch (e) {
                this.notify('error', 'Gagal: ' + ((e.response && e.response.data && e.response.data.message) || e.message));
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>
